"use client";

import i18n from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { i18nextConfig as i18nextConfigBase } from "./i18next.config";

export const SUPPORTED_LANGUAGES = [
  "cs",
  "da",
  "de",
  "en",
  "fr",
  "it",
  "nl",
  "pl",
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export interface I18nConfig {
  debug?: boolean;
  fallbackLng?: SupportedLanguage;
  supportedLngs?: typeof SUPPORTED_LANGUAGES;
  backend?: {
    loadPath: string;
  };
}

let isInitialized = false;

const i18nextConfig = {
  ...i18nextConfigBase,
  ns: ["common", "tournament", "accommodation", "guests", "auth", "booking", "bookings", "dispensation", "filter"],
  defaultNS: "common",
  fallbackNS: "common",
};

export const initI18next = async (config: I18nConfig = {}) => {
  if (typeof window === "undefined") return i18n;

  if (!isInitialized) {
    await i18n
      .use(Backend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        ...i18nextConfig,
        ...config,
        react: {
          useSuspense: false,
          bindI18n: "languageChanged loaded",
          bindI18nStore: "added removed",
          transEmptyNodeValue: "",
          transSupportBasicHtmlNodes: true,
          transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p"],
        },
      });
    isInitialized = true;
  }

  return i18n;
};

export const changeLanguage = (lang: SupportedLanguage) => {
  if (typeof window === "undefined") return Promise.resolve(i18n);
  return i18n.changeLanguage(lang);
};

// Helper function to change language based on language code
export const changeLanguageByCode = (languageCode: string) => {
  const lang = languageCode.toLowerCase();
  switch (lang) {
    case "cs":
    case "cz":
      return changeLanguage("cs");
    case "da":
    case "dk":
      return changeLanguage("da");
    case "de":
      return changeLanguage("de");
    case "fr":
      return changeLanguage("fr");
    case "it":
      return changeLanguage("it");
    case "nl":
      return changeLanguage("nl");
    case "pl":
      return changeLanguage("pl");
    default:
      return changeLanguage("en");
  }
};

// Language code to Accept-Language header mapping
export const getAcceptLanguageHeader = (languageCode: SupportedLanguage): string => {
  const languageMap: Record<SupportedLanguage, string> = {
    cs: "cs-CZ",
    da: "da-DK", 
    de: "de-DE",
    en: "en-US",
    fr: "fr-FR",
    it: "it-IT",
    nl: "nl-NL",
    pl: "pl-PL",
  };
  
  return languageMap[languageCode] || "en-US";
};

// Helper to get current language for API calls
export const getCurrentLanguageForAPI = (): string => {
  if (typeof window === "undefined") {
    return "en-US";
  }

  const currentLang = i18n.language as SupportedLanguage;
  const acceptLanguage = getAcceptLanguageHeader(currentLang || "en");

  return acceptLanguage;
};

// Export the i18n instance directly
export { i18n };